import { escapeHtml, initials } from '../ui.js';
import {
  waitingOnYou, yourList, yourListCounts, youreWaitingOn, waitingParty, teamRows,
  refFor, stateLine, dueText, daysLate, checkAge, waitAge, lastChased, isLate, isOpen, uiState,
  workingOnNow, dowDate, heldOutside, briefText, CHECK_HOT_DAYS, WAIT_HOT_DAYS,
} from '../tasks-v2.js';
import { sourceTag } from './task-row.js';

// THE TASKS PAGE, DRAWN — v4.0.0 (Karan's design export 22/10/2026, p08 Mine,
// p09 Team; docs/ui-refresh/01-home-workspace.md §4–§5).
//
// Pure: strings in, HTML out, every value through escapeHtml. The page module
// (tasks.js) owns the reads, the writes and the listeners; this file only says
// what Mine and Team look like, so a harness or a test can mount it without
// Firestore.
//
// Mine answers three questions in order — what is waiting on you, what is on
// your list, what are you waiting on. Team is the morning table with a figures
// strip over it. The brief rows, the anchor count and the orange + are gone
// (§0.4): the title carries the counts inline and New task is the one primary.

const e = escapeHtml;
const first = (name) => String(name || '').split(/\s+/)[0] || '';
const personOf = (people, id) => people.find((p) => p.userId === id) || null;
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const DASH = '<span class="tk4-none">–</span>';

/** Kept for callers of the old brief renderer: parts → escaped HTML. */
export function briefHtml(parts = []) {
  return parts.map((p) => {
    if (typeof p === 'string') return e(p);
    if (p && p.taskId) return `<a href="#" role="button" class="tk2-blink" data-open="${e(p.taskId)}">${e(p.text)}</a>`;
    if (p && p.hot) return `<span class="tk2-hot">${e(p.text)}</span>`;
    return e(p && p.text);
  }).join('');
}
export { briefText };

/** "8570 · I4" in the technical face, then the client in grey; or a grey "No project". */
export function refHtml(t, project) {
  const r = refFor(t, project);
  if (!r) return '<span class="tk4-ref"><span class="tk4-none">No project</span></span>';
  return `<span class="tk4-ref"><span class="tk4-num">${e(r.ref)}</span>${
    r.client ? ` <span class="tk4-client">${e(r.client)}</span>` : ''}</span>`;
}

/** Initials for anyone — a person, or an outside party ("Northfield Homes" → NH, "R3 Design" → R3). */
export function partyInitials(name) {
  const words = String(name || '').replace(/[^\p{L}\p{N}\s&]/gu, ' ').split(/\s+/).filter((w) => w && w !== '&');
  if (!words.length) return '?';
  if (words.length === 1 || words[0].length <= 2) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
const chip = (label, cls = '') => `<span class="av tk4-av${cls}" aria-hidden="true">${e(label)}</span>`;

/** The inline meta after the title: Mine's counts, or Team's head-count. */
export function headSub(view, { tasks = [], people = [], meId = null, today } = {}) {
  if (view === 'team') return `the team · ${plural(people.length, 'person', 'people')}`;
  const c = yourListCounts(tasks, meId, today);
  return [`${c.open} open`, c.overdue ? `${c.overdue} overdue` : '', c.doneToday ? `${c.doneToday} done today` : '']
    .filter(Boolean).join(' · ');
}

/** Title row: Tasks + inline meta; right, Mine | Team, Standup mode (Team), New task. */
export function headHtml({ view = 'mine', sub = '' } = {}) {
  const seg = (k, label) => `<button type="button" role="tab" class="${view === k ? 'active' : ''}"
    data-tab="${k}" aria-selected="${view === k}">${label}</button>`;
  return `<div class="tk4-head">
    <div class="page-h"><h1>Tasks</h1>${sub ? `<span class="page-sub">${e(sub)}</span>` : ''}</div>
    <div class="tk4-tools">
      <div class="seg" role="tablist" aria-label="Whose tasks">${seg('mine', 'Mine')}${seg('team', 'Team')}</div>
      ${view === 'team' ? '<button type="button" class="btn" data-standup>Standup mode</button>' : ''}
      <button type="button" class="btn primary" data-new>New task</button>
    </div>
  </div>`;
}

const secHead = (title, sub) => `<div class="tk4-sec-h"><h2 class="section-h">${e(title)}</h2>${
  sub ? `<span class="tk4-sec-sub">${e(sub)}</span>` : ''}</div>`;

/** "19/10 · 3 days late", "Today", "Mon 26/10". */
function whenText(t, today) {
  if (isLate(t, today)) return String(dueText(t, today)).replace(/\d+d late$/, `${plural(daysLate(t, today), 'day')} late`);
  return dueText(t, today);
}

// ── Mine ────────────────────────────────────────────────────────────────────

/**
 * @param vm.tasks     open work plus today's done (one list)
 * @param vm.people    the team (teamFrom)
 * @param vm.projects  { [projectId]: project }
 * @param vm.acted     { [taskId]: 'Chased' | 'Nudged' } — buttons already pressed
 * @param vm.openId    the task the panel shows, so its row is marked
 * @param vm.example   a project number for the add line's example
 */
export function mineHtml({ tasks = [], people = [], projects = {}, meId = null, today, acted = {}, openId = null, example = '' } = {}) {
  const proj = (t) => projects[t.projectId] || null;
  const on = (t) => (t.id === openId ? ' on' : '');

  // The add line leads the page (p08): Enter files a task, a leading number files it to that project.
  const eg = example ? `, e.g. ${example} chase beam drawings` : '';
  const add = `<label class="tk4-add">
      <span class="tk4-add-plus" aria-hidden="true">+</span>
      <input class="tk4-add-in" data-add type="text" autocomplete="off" maxlength="300"
        placeholder="${e(`Add a task — start with a project number to file it${eg}`)}" aria-label="Add a task">
      <span class="tk4-add-hint" aria-live="polite"></span>
      <kbd class="tk4-kbd">Enter</kbd>
    </label>`;

  // 1. Waiting on you
  const checks = waitingOnYou(tasks, meId, today);
  const checkRows = checks.map((t) => {
    const n = checkAge(t, today);
    const from = personOf(people, t.assigneeId);
    const verb = /^review\b/i.test(t.title || '') ? 'Review' : 'Check';
    return `<div class="tk4-row tk4-wy${on(t)}" data-id="${e(t.id)}">
      ${chip(from ? from.initials || initials(from.name) : '?')}
      <span class="tk4-task"><span class="tk4-title tk4-strong">${e(t.title)}</span>${sourceTag(t)}</span>
      ${refHtml(t, proj(t))}
      <span class="tk4-when${n >= CHECK_HOT_DAYS ? ' late' : ''}"${from ? ` title="From ${e(first(from.name))}"` : ''}>${n > 0 ? `waiting ${e(plural(n, 'day'))}` : 'today'}</span>
      <span class="tk4-act"><button type="button" class="btn sm" data-open="${e(t.id)}">${verb}</button></span>
    </div>`;
  }).join('');

  // 2. Your list
  const groups = yourList(tasks, meId, today);
  const c = yourListCounts(tasks, meId, today);
  const listRows = groups.map((g) => `<div class="tk4-group${g.key === 'overdue' ? ' late' : ''}">${e(g.label)} · ${g.tasks.length}</div>
    ${g.tasks.map((t) => {
      const done = t.state === 'done';
      return `<div class="tk4-row tk4-yl${done ? ' done' : ''}${on(t)}" data-id="${e(t.id)}">
        <button type="button" class="tk4-tick" data-tick="${e(t.id)}" role="checkbox" aria-checked="${done}"
          aria-label="${done ? 'Mark not done' : 'Mark done'}: ${e(t.title)}"></button>
        <span class="tk4-task"><span class="tk4-title">${e(t.title)}</span>${sourceTag(t)}</span>
        ${refHtml(t, proj(t))}
        <span class="tk4-when${!done && isLate(t, today) ? ' late' : ''}">${t.dueDate ? e(whenText(t, today)) : DASH}</span>
        <span class="tk4-act"></span>
      </div>`;
    }).join('')}`).join('');

  // 3. You're waiting on
  const waits = youreWaitingOn(tasks, meId, today);
  const waitRows = waits.map((t) => {
    const p = waitingParty(t, people);
    const n = waitAge(t, today);
    const chased = lastChased(t);
    const a = (t.state === 'blocked' ? 'chase' : 'nudge');
    const past = acted[t.id] || (chased === today ? (a === 'chase' ? 'Chased' : 'Nudged') : '');
    const detail = t.state === 'in_review'
      ? `${first(p.who)}, checking`
      : `${p.who}${p.detail ? `: ${p.detail}` : (p.what ? ` · ${p.what}` : '')}`;
    const chasedLine = chased ? `last ${a === 'chase' ? 'chased' : 'nudged'} ${String(chased).slice(8, 10)}/${String(chased).slice(5, 7)}`
      : `not ${a === 'chase' ? 'chased' : 'nudged'} yet`;
    return `<div class="tk4-row tk4-wo${on(t)}" data-id="${e(t.id)}">
      ${chip(p.person ? p.person.initials || initials(p.person.name) : partyInitials(p.who))}
      <span class="tk4-task"><span class="tk4-title"><b class="tk4-strong">${e(t.title)}</b> <span class="tk4-detail">— ${e(detail)}</span></span></span>
      ${refHtml(t, proj(t))}
      <span class="tk4-when tk4-when2"><span class="${n >= WAIT_HOT_DAYS ? 'late' : ''}">${e(n > 0 ? plural(n, 'day') : 'today')}</span><span class="tk4-chased">${e(chasedLine)}</span></span>
      <span class="tk4-act">${past
        ? `<span class="tk4-past">${e(past)}</span>`
        : `<button type="button" class="btn sm" data-${a}="${e(t.id)}">${a === 'chase' ? 'Chase' : 'Nudge'}</button>`}</span>
    </div>`;
  }).join('');

  return `${add}
    <section class="tk4-sec" aria-label="Waiting on you">
      ${secHead('Waiting on you', checks.length ? `${plural(checks.length, 'check')} · oldest first` : '')}
      <div class="tk4-list arrive">${checkRows || '<p class="tk4-empty">Nothing is waiting on your check.</p>'}</div>
    </section>
    <section class="tk4-sec" aria-label="Your list">
      ${secHead('Your list', `${c.open} open`)}
      <div class="tk4-list">${listRows || '<p class="tk4-empty">Nothing on your list. Type above to add something.</p>'}</div>
    </section>
    <section class="tk4-sec" aria-label="You’re waiting on">
      ${secHead('You’re waiting on', waits.length ? `${waits.length} · longest first` : '')}
      <div class="tk4-list">${waitRows || '<p class="tk4-empty">Nothing of yours is held by someone else.</p>'}</div>
    </section>`;
}

// ── Team ────────────────────────────────────────────────────────────────────

/** One expanded person's tasks: tick, title, project, state, due — and Assign
 *  on an unowned row. */
function personTasks(r, { people, projects, today, openId }) {
  const rows = r.tasks.slice().sort((a, b) => Number(isLate(b, today)) - Number(isLate(a, today))
    || String(a.dueDate || '9999').localeCompare(String(b.dueDate || '9999')));
  if (!rows.length) return '<p class="tk4-empty">No open tasks.</p>';
  return rows.map((t) => {
    const s = stateLine(t, people, today);
    return `<div class="tk4-row tk4-pt${t.id === openId ? ' on' : ''}" data-id="${e(t.id)}">
      <button type="button" class="tk4-tick" data-tick="${e(t.id)}" role="checkbox" aria-checked="false"
        aria-label="Mark done: ${e(t.title)}"></button>
      <span class="tk4-task"><span class="tk4-title">${e(t.title)}</span>${sourceTag(t)}</span>
      ${refHtml(t, projects[t.projectId] || null)}
      <span class="tk4-state tk4-tone-${e(s.tone)}">${e(s.text)}</span>
      <span class="tk4-when${isLate(t, today) ? ' late' : ''}">${t.dueDate ? e(whenText(t, today)) : DASH}</span>
      <span class="tk4-act">${r.unassigned ? `<button type="button" class="btn sm" data-assign="${e(t.id)}">Assign</button>` : ''}</span>
    </div>`;
  }).join('');
}

/** The four figures over the table (p09). */
export function teamFigures(tasks = [], people = [], today) {
  const open = tasks.filter(isOpen);
  const owners = new Set(open.map((t) => t.assigneeId).filter(Boolean));
  const overdue = open.filter((t) => uiState(t) === 'open' && isLate(t, today));
  const overduePeople = new Set(overdue.map((t) => t.assigneeId).filter(Boolean));
  const checks = open.filter((t) => t.state === 'in_review');
  const oldest = checks.length ? Math.max(...checks.map((t) => checkAge(t, today))) : 0;
  const held = open.filter(heldOutside);
  return [
    { label: 'Open tasks', value: open.length, note: `across ${plural(owners.size, 'person', 'people')}` },
    { label: 'Overdue', value: overdue.length, note: overdue.length ? plural(overduePeople.size, 'person', 'people') : 'nothing late', late: overdue.length > 0 },
    { label: 'Checks waiting', value: checks.length, note: checks.length ? (oldest > 0 ? `oldest ${plural(oldest, 'day')}` : 'all from today') : 'none queued' },
    { label: 'Held up', value: held.length, note: 'waiting on someone outside' },
  ];
}

/** "8591 Party wall award — schedule of condition", or "On leave until Mon 26/10". */
function workingCell(r, tasks, projects, backOn) {
  if (r.unassigned) return '<span class="tk4-none">Needs an owner</span>';
  if (r.today === 'On leave') return e(backOn ? `On leave until ${dowDate(backOn)}` : 'On leave');
  const t = workingOnNow(tasks, r.person.userId);
  if (!t) return DASH;
  const p = projects[t.projectId];
  return `${p && p.number ? `<span class="tk4-num">${e(p.number)}</span> ` : ''}${e(t.title)}`;
}

/** Held up by: the oldest blocker's project and party, "+N" when there are more. */
function heldCell(r, projects, people, today) {
  const blocked = r.tasks.filter((t) => t.state === 'blocked').sort((a, b) => waitAge(b, today) - waitAge(a, today));
  if (!blocked.length) return DASH;
  const t = blocked[0];
  const p = waitingParty(t, people);
  const num = (projects[t.projectId] || {}).number;
  const text = [p.who, p.detail].filter(Boolean).join(' ');
  return `${num ? `<span class="tk4-num">${e(num)}</span> ` : ''}${e(text)}${blocked.length > 1 ? ` <span class="tk4-more">+${blocked.length - 1}</span>` : ''}`;
}

/**
 * @param vm.expanded  Set of row keys ('karan', '_none') shown open
 * @param vm.backOn    { [userId]: 'YYYY-MM-DD' } — the day someone on leave is back
 */
export function teamHtml({ tasks = [], people = [], projects = {}, meId = null, today, expanded = new Set(), openId = null, backOn = {} } = {}) {
  const rows = teamRows(tasks, people, today, meId);
  const figs = teamFigures(tasks, people, today);
  const body = rows.map((r) => {
    const key = r.key || '_none';
    const isOn = expanded.has(key);
    const name = r.unassigned ? 'Unassigned' : r.person.name;
    const role = r.unassigned ? 'tasks with no owner' : r.person.role || '';
    return `<div class="tk4-prow${r.unassigned ? ' none' : ''}${isOn ? ' on' : ''}" data-person="${e(key)}"
        role="button" tabindex="0" aria-expanded="${isOn}">
      <span class="tk4-pname">${chip(r.unassigned ? '–' : r.person.initials || initials(name), r.me ? ' me' : '')}
        <span class="tk4-pwho"><b>${e(name)}${r.me ? ' <span class="tk4-you">(you)</span>' : ''}</b>${role ? `<span>${e(role)}</span>` : ''}</span></span>
      <span class="tk4-now" data-l="Working on now">${workingCell(r, tasks.filter(isOpen), projects, r.person && backOn[r.person.userId])}</span>
      <span class="tk4-n" data-l="Open">${r.open}</span>
      <span class="tk4-n${r.overdue ? ' late' : ''}" data-l="Overdue">${r.overdue || DASH}</span>
      <span class="tk4-n" data-l="Checks">${r.checks || DASH}</span>
      <span class="tk4-held" data-l="Held up by">${heldCell(r, projects, people, today)}</span>
    </div>
    ${isOn ? `<div class="tk4-ptasks">${personTasks(r, { people, projects, today, openId })}</div>` : ''}`;
  }).join('');

  return `<div class="figs tk4-figs">${figs.map((f) => `<div class="fig">
      <div class="fig-label">${e(f.label)}</div>
      <div class="fig-value${f.late ? ' late' : ''}">${e(f.value)}</div>
      <div class="fig-note">${e(f.note)}</div></div>`).join('')}</div>
    <section class="tk4-team" aria-label="The team">
      <div class="tk4-cols" aria-hidden="true"><span>Person</span><span>Working on now</span><span class="r">Open</span><span class="r">Overdue</span><span class="r">Checks</span><span>Held up by</span></div>
      <div class="tk4-tbody arrive">${body || '<p class="tk4-empty">Nobody on the team yet.</p>'}</div>
      <p class="tk4-help">Click a person to see their list and reassign. In standup mode, the list goes full screen and you can add tasks for anyone as you talk.</p>
    </section>`;
}

/** The rail/aria count: your open list on Mine, the whole team's open work on Team. */
export function headCount(view, tasks, meId, today) {
  return view === 'team' ? tasks.filter(isOpen).length : yourListCounts(tasks, meId, today).open;
}

/** Which view opens: Mine, unless the person last chose Team. The old page
 *  stored team / board / mine / projects here; everything but 'team' is Mine. */
export const viewFrom = (stored) => (stored === 'team' ? 'team' : 'mine');

/** What the add line will do with what has been typed, for the hint beside it. */
export function addHint(parsed) {
  if (!parsed) return '';
  if (parsed.project) {
    const r = refFor({}, parsed.project);
    return `Files to ${r.ref}${r.client ? ` ${r.client}` : ''}`;
  }
  return 'No project';
}
